import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import MemoryIcon from '@material-ui/icons/Memory';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: '100%',
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  linkOpen: {
    marginLeft: 24,
  },
  linkClosed: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
});

const useRootStyles = makeStyles(theme => ({
  // Green divider line between Search and Menu - full width, solid color, no effects
  divider: {
    height: 1,
    border: 'none',
    margin: theme.spacing(1.2, 0),
    background: theme.palette.primary.main,
  },
  // Decorative green wave at the bottom of the sidebar
  footerWave: {
    width: '100%',
    height: 96,
    flexShrink: 0,
    marginTop: theme.spacing(1),
    pointerEvents: 'none',
    display: 'block',
  },
}));

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        to="/"
        underline="none"
        className={isOpen ? classes.linkOpen : classes.linkClosed}
        aria-label="Home"
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const classes = useRootStyles();
  return (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider className={classes.divider} />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={TrackChangesIcon} to="tech-radar" text="Tech radar" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        <SidebarItem icon={MemoryIcon} to="mcp" text="AI / MCP" />
        {/* End global nav */}
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <NotificationsSidebarItem />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
      {/* Decorative GasNet green wave at the bottom of the sidebar */}
      <svg
        className={classes.footerWave}
        viewBox="0 0 224 96"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0,96 L0,58 Q56,26 124,48 T224,36 L224,96 Z"
          fill="rgba(64, 196, 0, 0.10)"
        />
        <path
          d="M0,96 L0,74 Q66,48 136,66 T224,60 L224,96 Z"
          fill="rgba(64, 196, 0, 0.18)"
        />
      </svg>
    </Sidebar>
    {children}
  </SidebarPage>
  );
};
